import {Howl} from "howler";
import {DateUtils} from "../utils/DateUtils";
import {AbstractApp} from "../app/AbstractApp";
import {AbstractPreferencesManager} from "./AbstractPreferencesManager";

export class SoundManager extends AbstractPreferencesManager {

  static SOUND_VOLUME = 0.8;
  static MUSIC_VOLUME = 0.6;

  static Click = "Click";
  static DialogOpen = "DialogOpen";
  static DialogClose = "DialogClose";

  protected howlerSoundPool: Map<string, Howl> = new Map();
  protected howlMusic: Howl | null = null;
  protected musicName = '';
  protected soundLastPlayed: Map<string, number> = new Map();

  readonly app: AbstractApp;

  protected static SOUND_MUTED_KEY = "SOUND_MUTED_KEY";
  protected static MUSIC_MUTED_KEY = "MUSIC_MUTED_KEY";

  protected _soundMuted = false;
  protected _musicMuted = false;
  protected _needPersistence:boolean;

  constructor(app: AbstractApp, needPersistence: boolean) {
    super();
    this.app = app;
    this._needPersistence = needPersistence;
  }

  async init() {
    if(this._needPersistence) {
      this._soundMuted = await this.getBoolValue(SoundManager.SOUND_MUTED_KEY);
      this._musicMuted = await this.getBoolValue(SoundManager.MUSIC_MUTED_KEY);
    }
  }

  async playSound(soundName: string, timeGapBetweenNextSameSound: number = 100) {
    if (this.soundMuted) {
      return;
    }

    // Проверяем время последнего воспроизведения этого звука
    const lastPlayedTime = this.soundLastPlayed.get(soundName) || 0;
    const currentTime = DateUtils.currentTime;

    // Если прошло достаточно времени с последнего воспроизведения, играем звук
    if (currentTime - lastPlayedTime >= timeGapBetweenNextSameSound) {

      if(!this.howlerSoundPool.has(soundName)) {
        const sound = new Howl({
          src: [`assets/sounds/${soundName}.mp3`],
          volume: SoundManager.SOUND_VOLUME,
          html5: false,
        });
        this.howlerSoundPool.set(soundName, sound);
      }
      this.howlerSoundPool.get(soundName)?.play();

      this.soundLastPlayed.set(soundName, currentTime); // Обновляем время последнего воспроизведения
    }
  }

  async onActivate() {
    this.playMusic(this.musicName);
  }

  async onDeActivate() {
    this.unloadSounds();
    this.unloadMusic();
    Howler.stop();
    Howler.unload();
  }

  playMusic(musicName: string) {
    if (!musicName) {
      return;
    }

    if (this._musicMuted) {
      this.musicName = musicName;
      return;
    }

    if (this.musicName === musicName && this.howlMusic) {
      return;
    }

    this.unloadMusic();

    this.musicName = musicName;

    try {
      this.howlMusic = new Howl({
        src: [`assets/music/${musicName}.mp3`],
        volume: SoundManager.MUSIC_VOLUME,
        loop: true,
        html5: false,
      });
      this.howlMusic.play();
      this.howlMusic.loop();
    } catch (error) {
      this.app.error.handleCaughtError(error, 'SoundService:playMusic: ' + musicName);
    }
  }

  unloadMusic() {
    try {
      if (this.howlMusic) {
        this.howlMusic.stop();
        this.howlMusic.unload();
        this.howlMusic = null;
      }
    }
    catch (error) {
      this.app.error.handleCaughtError(error, 'SoundService:unloadMusic');
    }
  }

  unloadSounds(): void {
    try {
      this.howlerSoundPool.forEach((sound) => {
        sound.stop();
        sound.unload();
      });
      this.howlerSoundPool.clear();
    }
    catch (error) {
      this.app.error.handleCaughtError(error, 'SoundService:unloadSounds');
    }
  }

  get soundMuted(): boolean {
    return this._soundMuted;
  }

   set soundMuted(value: boolean) {
    if (this._soundMuted != value) {
      this._soundMuted = value;
      if (this._needPersistence) {
        void this.setBoolValue(SoundManager.SOUND_MUTED_KEY, value);
      }
      if (this._soundMuted) {
        this.unloadSounds();
      }
    }
  }

  async unmute() {
    if (this._musicMuted) {
      this._musicMuted = false;
      if (this._needPersistence) {
        void this.setBoolValue(SoundManager.MUSIC_MUTED_KEY, false);
      }
      if (this.musicName) {
        this.playMusic(this.musicName);
      }
    }
  }

  async mute() {
    if (!this._musicMuted) {
      this._musicMuted = true;
      if (this._needPersistence) {
        void this.setBoolValue(SoundManager.MUSIC_MUTED_KEY, true);
      }
      this.unloadMusic();
    }
  }
}
